import { Injectable, inject } from '@angular/core';
import { UploadObj, UploadObjError } from '../classes/upload-obj.class';
import { GROWTHBOOK } from '../growthbook';
import { getFileType } from '../lib/get-file-type';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { SentryService } from './sentry.service';

@Injectable({
  providedIn: 'root',
})
export class UploadAnalyticsService {
  private analytics = inject(PICFLOW_ANALYTICS);
  private growthBook = inject(GROWTHBOOK);
  private sentryService = inject(SentryService);

  public reportUploadStarted(uploads: UploadObj[], uploadId: string) {
    this.analytics.track('upload_started', true, {
      uploads: uploads.length,
      uploadId,
      totalSize: uploads.reduce((t, upload) => t + upload.file.size, 0),
    });
  }

  public reportUploadBeforeClose(uploads: UploadObj[], uploadId: string) {
    this.analytics.track('upload_onbeforeunload', false, { uploadId });
  }

  public reportUploadCompleted(uploads: UploadObj[], uploadId: string, startTime: number) {
    this.analytics.track('upload_completed', false, {
      cancelled: uploads.some(upload => upload.cancelled),
      uploads: uploads.length,
      uploadId,
      duration: Date.now() - startTime,
      accelerated: this.growthBook.isOn('accelerated-upload'),
      ...this.getUploadAnalyticsData(uploads),
    });
  }

  private getUploadAnalyticsData(uploads: UploadObj[]) {
    const successCount = uploads.filter(upload => upload.status === 'done').length;

    const formats = this.getCountObject(uploads, upload => getFileType(upload.file));
    const format = Object.keys(formats).sort((a, b) => formats[b] - formats[a])[0];

    const errorsAndWarnings = this.getCountObject(uploads, upload =>
      upload.error === 'cancelled' ? undefined : upload.error
    );
    const warningKeys: UploadObjError[] = ['invalid-file-type', 'file-too-big'];

    const errorCount = Object.keys(errorsAndWarnings).reduce((t, key) => {
      if (!warningKeys.includes(key as UploadObjError)) return t + errorsAndWarnings[key];
      return t;
    }, 0);

    if (errorCount) this.sentryService.captureException('errors_in_upload');

    const warningCount = Object.keys(errorsAndWarnings).reduce((t, key) => {
      if (warningKeys.includes(key as UploadObjError)) return t + errorsAndWarnings[key];
      return t;
    }, 0);

    const uploadsToCountForSpeed = uploads.filter(
      upload => upload.status === 'done' && !upload.cancelled && !('source' in upload.file)
    );

    const timeSpentUploading = this.calcTotalTime(
      uploadsToCountForSpeed.map(upload => ({
        start: upload.timings.upload_started_at,
        end: upload.timings.upload_finished_at,
      }))
    );

    const timeSpentCreating = this.calcTotalTime(
      uploadsToCountForSpeed.map(upload => ({
        start: upload.timings.asset_creation_started_at,
        end: upload.timings.asset_creation_finished_at,
      }))
    );

    const totalFileSizeMB = uploads.reduce((t, upload) => {
      if ('source' in upload.file) return; // only count files that were uploaded locally
      return t + upload.file.size / 1024 / 1024;
    }, 0);
    const totalFileSizeMBUploaded = uploadsToCountForSpeed.reduce((t, upload) => {
      if (upload.status !== 'done') return t;
      if ('source' in upload.file) return; // only count files that were uploaded locally
      return t + upload.file.size / 1024 / 1024;
    }, 0);

    const uploadSpeedMbit = (totalFileSizeMBUploaded / (timeSpentUploading / 1000)) * 8;

    return {
      errorsAndWarnings,
      errorCount,
      warningCount,
      successCount,
      formats,
      format,
      errorFormats: this.getCountObject(
        uploads,
        upload => upload.error && !warningKeys.includes(upload.error) && getFileType(upload.file)
      ),
      warningFormats: this.getCountObject(
        uploads,
        upload => upload.error && warningKeys.includes(upload.error) && getFileType(upload.file)
      ),
      totalFileSizeMB,
      totalFileSizeMBUploaded,
      timeSpentCreating,
      timeSpentUploading,
      uploadSpeedMbit,
    };
  }

  private calcTotalTime(timings: { start: number; end: number }[]) {
    const uniqueTimes: { start: number; end: number }[] = [];

    timings.forEach(timing => {
      const foundInside = uniqueTimes.find(
        uniqueTiming => uniqueTiming.start <= timing.start && uniqueTiming.end >= timing.end
      );
      if (foundInside) return;

      const foundPartial = uniqueTimes.find(uniqueTiming => {
        if (uniqueTiming.start <= timing.start && uniqueTiming.end >= timing.start) return true;
        if (uniqueTiming.start <= timing.end && uniqueTiming.end >= timing.end) return true;
        if (uniqueTiming.start >= timing.start && uniqueTiming.end <= timing.end) return true;
        return false;
      });

      if (!foundPartial) {
        uniqueTimes.push(timing);
      } else {
        foundPartial.start = Math.min(foundPartial.start, timing.start);
        foundPartial.end = Math.max(foundPartial.end, timing.end);
      }
    });

    return uniqueTimes.reduce((total, timing) => total + timing.end - timing.start, 0);
  }

  private getCountObject<T>(array: T[], method: (a: T) => string | undefined) {
    const errorTypeCount = new Map<string, number>();
    array.forEach(upload => {
      const value = method(upload);
      if (!value) return;
      const count = errorTypeCount.get(value) || 0;
      errorTypeCount.set(value, count + 1);
    });

    return Object.fromEntries(errorTypeCount);
  }
}
